/**
 * Creates topic-less ContentPublication drafts for sprint reactive slots.
 * Used as a client-side repair for strategies created before the backend
 * emitted reactive/manual slot publications directly.
 */

import type {
  ContentPublication,
  ContentSprint,
  ContentStrategy,
  PlatformId,
} from '../types';
import {
  assignStrategyPublicationSlot,
  listStrategyPublicationSlotsForWeek,
} from './strictStrategySlots';
import { stripUndefined } from './stripUndefined';

export interface ReactivePublicationBackfillInputs {
  projectId: string;
  strategy: Pick<ContentStrategy, 'id'> & {
    dateStart?: string | Date;
    platformCadences?: unknown;
  };
  sprints: ReadonlyArray<Pick<ContentSprint, 'id'> & {
    dateStart?: string | Date;
    dateEnd?: string | Date;
    reactiveSlotsCount?: number;
  }>;
  targetPlatforms: ReadonlyArray<PlatformId>;
  existingPublications: ReadonlyArray<
    Pick<ContentPublication, 'id'> &
    Partial<Pick<ContentPublication, 'platform' | 'scheduledAt'>>
  >;
}

function toIsoDate(value: string | Date | undefined): string {
  if (!value) return '';
  if (value instanceof Date) return value.toISOString().slice(0, 10);
  return String(value).slice(0, 10);
}

function buildWeekWindows(
  sprintStartIso: string,
  sprintEndIso: string,
): Array<{ startIso: string; endIso: string }> {
  const sprintStart = new Date(`${sprintStartIso.slice(0, 10)}T00:00:00.000Z`);
  const sprintEnd = new Date(`${sprintEndIso.slice(0, 10)}T00:00:00.000Z`);
  if (Number.isNaN(sprintStart.getTime()) || Number.isNaN(sprintEnd.getTime()) || sprintEnd < sprintStart) {
    return [];
  }
  const windows: Array<{ startIso: string; endIso: string }> = [];
  const cursor = new Date(sprintStart);
  while (cursor <= sprintEnd) {
    const windowStart = new Date(cursor);
    const windowEnd = new Date(cursor);
    windowEnd.setUTCDate(windowEnd.getUTCDate() + 6);
    if (windowEnd > sprintEnd) {
      windowEnd.setTime(sprintEnd.getTime());
    }
    windows.push({
      startIso: windowStart.toISOString().slice(0, 10),
      endIso: windowEnd.toISOString().slice(0, 10),
    });
    cursor.setUTCDate(cursor.getUTCDate() + 7);
  }
  return windows;
}

function startOfIsoWeekKey(value: string | undefined): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  date.setUTCHours(0, 0, 0, 0);
  const mondayOffset = (date.getUTCDay() + 6) % 7;
  date.setUTCDate(date.getUTCDate() - mondayOffset);
  return date.toISOString().slice(0, 10);
}

export function backfillReactivePublicationsFromSprints(
  input: ReactivePublicationBackfillInputs,
): ContentPublication[] {
  const platforms = Array.from(new Set(
    input.targetPlatforms
      .map((platform) => String(platform ?? '').trim())
      .filter(Boolean),
  )) as PlatformId[];
  if (platforms.length === 0) return [];

  const existingIds = new Set(input.existingPublications.map((publication) => publication.id));
  const strategyStartIso = toIsoDate(input.strategy.dateStart);
  const strategyStart = strategyStartIso ? new Date(`${strategyStartIso}T00:00:00.000Z`).getTime() : 0;
  const cadences = input.strategy.platformCadences ?? null;
  const toCreate: ContentPublication[] = [];
  const slotCountersByWeekPlatform = new Map<string, number>();
  const hasExactCadenceForEveryPlatform = platforms.every((platform) =>
    listStrategyPublicationSlotsForWeek({
      platform,
      weekStartDate: '2026-01-05',
      weekEndDate: '2026-01-11',
      cadences: cadences as any,
    }).length > 0,
  );

  for (const publication of input.existingPublications) {
    if (!publication.platform || !publication.scheduledAt) continue;
    const weekStartIso = startOfIsoWeekKey(publication.scheduledAt);
    if (!weekStartIso) continue;
    const key = `${weekStartIso}:${publication.platform}`;
    slotCountersByWeekPlatform.set(key, (slotCountersByWeekPlatform.get(key) ?? 0) + 1);
  }

  for (const sprint of input.sprints) {
    const reactiveCount = Math.max(0, Math.floor(Number(sprint.reactiveSlotsCount ?? 0) || 0));
    if (reactiveCount === 0 && !hasExactCadenceForEveryPlatform) continue;
    const sprintStart = toIsoDate(sprint.dateStart);
    const sprintEnd = toIsoDate(sprint.dateEnd);
    if (!sprintStart || !sprintEnd) continue;
    const publicationWeeks = buildWeekWindows(sprintStart, sprintEnd);
    if (publicationWeeks.length === 0) continue;

    if (hasExactCadenceForEveryPlatform) {
      // Exact cadence strategies are emitted by the backend from
      // sprint.assignments[] + reactiveSlotsCount. The client must not turn
      // every unused cadence slot into a manual placeholder, or it will inflate
      // manual slots beyond the strategy's reactive percentage.
      continue;
    }

    for (let reactiveSlotIndex = 1; reactiveSlotIndex <= reactiveCount; reactiveSlotIndex += 1) {
      const publicationWeek = publicationWeeks[(reactiveSlotIndex - 1) % publicationWeeks.length];
      const weekStartMs = new Date(`${publicationWeek.startIso}T00:00:00.000Z`).getTime();
      const weekIndex = strategyStart > 0
        ? Math.max(0, Math.floor((weekStartMs - strategyStart) / (7 * 86_400_000)))
        : 0;

      for (const platform of platforms) {
        const id = `pub_${sprint.id}_reactive_${reactiveSlotIndex}_${platform}`;
        if (existingIds.has(id)) continue;

        const slotCounterKey = `${publicationWeek.startIso}:${platform}`;
        const platformSlotIndex = slotCountersByWeekPlatform.get(slotCounterKey) ?? 0;
        slotCountersByWeekPlatform.set(slotCounterKey, platformSlotIndex + 1);
        let slot;
        try {
          slot = assignStrategyPublicationSlot({
            fallbackPostId: `${sprint.id}_reactive_${reactiveSlotIndex}_${platform}`,
            platform,
            weekStartDate: publicationWeek.startIso,
            weekEndDate: publicationWeek.endIso,
            cadences: cadences as any,
            slotIndex: platformSlotIndex,
          });
        } catch {
          continue;
        }

        toCreate.push(stripUndefined({
          id,
          projectId: input.projectId,
          strategyId: input.strategy.id,
          sprintId: sprint.id,
          platform,
          scheduledAt: slot.scheduledAt,
          weekIndex,
          weekdayIndex: slot.weekdayIndex,
          source: 'reactive',
          status: 'planned',
          reactiveSlotIndex,
        } as ContentPublication));
      }
    }
  }

  return toCreate;
}
